import { lazy } from "react";

import { Navigate } from "react-router-dom";
import Loadable from "../components/common/Loadable";
import CampaignTab from "../components/management/historyLog/CampaignTab";
import ConfigurationTab from "../components/management/historyLog/ConfigurationTab";
import ContentTab from "../components/management/historyLog/ContentTab";
import PlatformSegmentsTab from "../components/segmentation/PlatformSegmentsTab";
import SegmentDetailsPage from "../components/segmentation/SegmentDetailsPage";
import SegmentDataTab from "../components/segmentation/SegmentsDataTab";
import SegmentsListTab from "../components/segmentation/SegmentsListTab";
import { BannerProvider } from "../context/BannerContext";
import { CampaignSetupProvider } from "../context/CampaignSetupContext";
import { SurveyProvider } from "../context/SurveyContext";
import Layout from "../layout/Layout";
import { NotFoundPage } from "../pages/NotFoundPage";
import BonusListPage from "../pages/bonus/BonusListPage";
import ActivitiesListOutlet from "../pages/campaigns/setupPages/ActivitiesListOutlet";
import CodeGroupsListPage from "../pages/codeGroups/CodeGroupsListPage";
import ContentOutlet from "../pages/content/ContentOutlet";
import ContentDetailsPage from "../pages/content/contentDetails/ContentDetailsPage";
import CustomerSupportOutlet from "../pages/customerSupport/CustomerSupportOutlet";
import CodeSearchPage from "../pages/customerSupport/codeSearch/CodeSearch";
import CustomizedRewardDetailsPage from "../pages/customizedRewards/CustomizedRewardDetailsPage";
import CustomizedRewardsListPage from "../pages/customizedRewards/CustomizedRewardsListPage";
import AppConfigurationsEditPage from "../pages/management/AppConfigurationsEditPage";
import AppConfigurationsPage from "../pages/management/AppConfigurationsPage";
import GenericConfigurationEditPage from "../pages/management/GenericConfiguration/GenericConfigurationEditPage";
import GenericConfigurationViewPage from "../pages/management/GenericConfiguration/GenericConfigurationViewPage";
import HistoryLogsDetailPage from "../pages/management/HistoryLogs/HistoryLogsDetailPage";
import HistoryLogsListPage from "../pages/management/HistoryLogs/HistoryLogsListPage";
import KeywordManagementPage from "../pages/management/KeywordManagement/KeywordManagementPage";
import ManagementOutlet from "../pages/management/ManagementOutlet";
import OutcomeConfigurationEditPage from "../pages/management/OutcomeConfiguration/OutcomeConfigurationEditPage";
import OutcomeConfigurationViewPage from "../pages/management/OutcomeConfiguration/OutcomeConfigurationViewPage";
import PartnerConfigurationEditPage from "../pages/management/PartnerConfiguration/PartnerConfigurationEditPage";
import PartnerConfigurationViewPage from "../pages/management/PartnerConfiguration/PartnerConfigurationViewPage";
import SocialMediaLinksEditPage from "../pages/management/SocialMediaLinks/SocialMediaLinksEditPage";
import SocialMediaLinksPage from "../pages/management/SocialMediaLinks/SocialMediaLinksPage";
import SystemSettingPage from "../pages/management/SystemSettingPage";
import TermAndConditionEditPage from "../pages/management/TermAndConditionEditPage";
import TermAndConditionPage from "../pages/management/TermAndConditionPage";
import TermAndConditionViewPage from "../pages/management/TermAndConditionViewPage";
import UserLimitSettingsEditPage from "../pages/management/UserLimitSettings/UserLimitSettingsEditPage";
import UserLimitSettingsPage from "../pages/management/UserLimitSettings/UserLimitSettingsPage";
import MemberOutlet from "../pages/member/MemberOutlet";
import SegmentListPage from "../pages/organizations/Segmentation/SegmentListPage";
import SegmentSummaryViewPage from "../pages/organizations/Segmentation/SegmentSummaryView/SegmentSummaryViewPage";
import CodeGroupsOutlet from "../pages/partners/CodeGroupsOutlet";
import PartnersPage from "../pages/partners/PartnersPage";
import PerformanceDetailsPage from "../pages/performances/PerformanceDetailsPage";
import PerformancesListPage from "../pages/performances/PerformancesListPage";
import PerformancesOutlet from "../pages/performances/PerformancesOutlet";
import ReceiptScanPerformance from "../pages/performances/ReceiptScanPerformance";
import ProductDetailPage from "../pages/productManagement/ProductDetailPage";
import ProductManagementOutlet from "../pages/productManagement/ProductManagementOutlet";
import ProductManagementPage from "../pages/productManagement/ProductManagementPage";
import RewardsOutlet from "../pages/rewards/RewardsOutlet";
import StoreDetailPage from "../pages/stores/StoreDetailPage";
import StoreManagementOutlet from "../pages/stores/StoreManagementOutlet";
import StoreManagementPage from "../pages/stores/StoreManagementPage";
import SurveyListPage from "../pages/survey/SurveyListPage";
import SurveyOutlet from "../pages/survey/SurveyOutlet";
import SurveyManagementListPage from "../pages/surveyManagement/SurveyManagementListPage";
import SurveyManagementOutlet from "../pages/surveyManagement/SurveyManagementOutlet";
import SweepstakesOutlet from "../pages/sweepstakes/SweepstakesOutlet";
import TierManagementOutlet from "../pages/tierManagement/TierManagementOutlet";
import TierManagementPage from "../pages/tierManagement/TierManagementPage";
import WidgetsOutlet from "../pages/widgets/WidgetsOutlet";
import WinningRewardsScreen from "../pages/winningRewardsScreen/WinningRewardsScreen";
import { paths } from "./paths";

// dashboard
const DashboardPage = Loadable(lazy(() => import("../pages/dashboard")));

// campaign
const CampaignsPage = Loadable(
  lazy(() => import("../pages/campaigns/CampaignsPage"))
);
const CampaignListPage = Loadable(
  lazy(() => import("../pages/campaigns/CampaignListPage"))
);
const CampaignSummaryViewPage = Loadable(
  lazy(() => import("../pages/campaigns/CampaignSummaryViewPage"))
);

const CampaignCalendarPage = Loadable(
  lazy(() => import("../pages/campaigns/CampaignCalendarPage"))
);
const GenericSetupPage = Loadable(
  lazy(() => import("../pages/campaigns/setupPages/GenericSetupPage"))
);
const CampaignSetupOutletPage = Loadable(
  lazy(() => import("../pages/campaigns/setupPages"))
);
const SegmentSetupPage = Loadable(
  lazy(() => import("../pages/campaigns/setupPages/SegmentSetupPage"))
);
const ActivitiesSetupPage = Loadable(
  lazy(() => import("../pages/campaigns/setupPages/ActivitiesSetupPage"))
);
const ActivitySetupPage = Loadable(
  lazy(() => import("../pages/campaigns/setupPages/ActivitySetupPage"))
);
const ActivitiesListPage = Loadable(
  lazy(() => import("../pages/campaigns/setupPages/ActivitiesListPage"))
);
const CampaignLevelSetupPage = Loadable(
  lazy(() => import("../pages/campaigns/setupPages/CampaignLevelSetupPage"))
);
const RulesAndOutcomesPage = Loadable(
  lazy(
    () =>
      import(
        "../pages/campaigns/setupPages/rulesAndOutcomesPage/RulesAndOutcomesPage"
      )
  )
);
const SummaryPage = Loadable(
  lazy(() => import("../pages/campaigns/setupPages/SummaryPage"))
);

const RewardsPage = Loadable(lazy(() => import("../pages/rewards/RewardsPage")));

const CreateCustomisedEarnings = Loadable(
  lazy(
    () =>
      import(
        "../pages/CustomisedEarnings/CreateCustomisedEarnings/CreateCustomisedEarnings"
      )
  )
);
const RewardsRedemptionList = Loadable(
  lazy(() => import("../pages/rewardsRedemption/RewardsRedemption"))
);

const RewardsCatalogsListPage = Loadable(
  lazy(() => import("../pages/RewardsCatalogs/CatalogsList/RewardsCatalogsListPage"))
);

const ArchivedCatalogsPage = Loadable(
  lazy(() => import("../pages/RewardsCatalogs/CatalogsList/ArchivedCatalogsPage"))
);

const CatalogDetails = Loadable(
  lazy(() => import("../pages/RewardsCatalogs/CatalogDetails/CatalogDetails"))
);

const ProductsPage = Loadable(lazy(() => import("../pages/products/ProductsPage")));

const BannersListPage = Loadable(
  lazy(() => import("../pages/banners/BannersListPage"))
);

const WidgetListPage = Loadable(
  lazy(() => import("../pages/widgets/widgetList/WidgetListPage"))
);

const WidgetDetailPage = Loadable(
  lazy(() => import("../components/management/WidgetPages/WidgetDetailPage"))
);

const MemberHistoryPage = Loadable(
  lazy(
    () => import("../pages/widgets/widgetCreate/MemberHistoryPage/MemberHistoryPage")
  )
);

const MemberInformationPage = Loadable(
  lazy(
    () =>
      import(
        "../pages/widgets/widgetCreate/MemberInformationPage/MemberInformationPage"
      )
  )
);
const ListOfSweepstakesPage = Loadable(
  lazy(
    () =>
      import(
        "../pages/widgets/widgetCreate/ListOfSweepstakesPage/ListOfSweepstakesPage"
      )
  )
);
const ListOfCampaignsPage = Loadable(
  lazy(
    () =>
      import("../pages/widgets/widgetCreate/ListOfCampaignsPage/ListOfCampaignsPage")
  )
);

// const ActiveSweepstakesPage = Loadable(
//   lazy(() => import("../pages/sweepstakes/active/ActiveSweepstakesPage"))
// );
const SweepstakesEntrantsWinnersPage = Loadable(
  lazy(
    () =>
      import(
        "../pages/sweepstakes/sweepstakesEntrantsWinners/SweepstakesEntrantsWinnersPage"
      )
  )
);
const SweepstakesPickWinnersPage = Loadable(
  lazy(
    () =>
      import(
        "../pages/sweepstakes/sweepstakesPickWinners/SweepstakesPickWinnersPage"
      )
  )
);
const ArchivesListPage = Loadable(
  lazy(() => import("../pages/sweepstakes/archives/ArchivesListPage"))
);
const SweepstakesDetailsPage = Loadable(
  lazy(
    () => import("../pages/sweepstakes/sweepstakesDetails/SweepstakesDetailsPage")
  )
);
const SweepstakesDrawsAndWinnersPage = Loadable(
  lazy(
    () =>
      import(
        "../pages/sweepstakes/sweepstakesDrawsWinners/SweepstakesDrawsAndWinnersPage"
      )
  )
);
const DrawDetailsPage = Loadable(
  lazy(() => import("../pages/sweepstakes/drawDetails/DrawDetailsPage"))
);

//Member relationship pages

const MemberRelationListPage = Loadable(
  lazy(() => import("../pages/member/MemberListPage"))
);
const MemberRelationDetailPage = Loadable(
  lazy(() => import("../pages/member/MemberRelationDetailPage"))
);
const MemberParticipationDetailsPage = Loadable(
  lazy(
    () =>
      import("../pages/member/memberParticipation/MemberParticipationDetailsPage")
  )
);

// Content Pages

const ContentListPage = Loadable(
  lazy(() => import("../pages/content/contentList/ContentListPage"))
);

const ContentCreationPage = Loadable(
  lazy(() => import("../pages/content/ContentCreation/ContentCreationPage"))
);

//Segmentation Pages
const SegmentOutlet = Loadable(
  lazy(() => import("../pages/organizations/SegmentOutlet"))
);

const CommonContentPage = Loadable(
  lazy(() => import("../pages/content/contentManagement/CommonContentPage"))
);
// Leaderboard pages
const LeaderboardsOutlet = Loadable(
  lazy(() => import("../pages/leaderboards/LeaderboardsOutlet"))
);
const LeaderboardsPage = Loadable(
  lazy(() => import("../pages/leaderboards/LeaderboardsPage"))
);
const LeaderboardsDetailsPage = Loadable(
  lazy(() => import("../pages/leaderboards/LeaderboardsDetailsPage"))
);
const ArchivedLeaderboardsPage = Loadable(
  lazy(() => import("../pages/leaderboards/ArchivedLeaderboardsPage"))
);

// FULFILLMENT RULES PAGE.

const FullfillmentRulesPage = Loadable(
  lazy(() => import("../pages/fullfillmentRules/FullfillmentRulesPage"))
);

const FulfillmentOrdersPage = Loadable(
  lazy(() => import("../pages/fulfillmentOrders/FulfillmentOrdersPage"))
);

// Winning Schedules pages
const WinningSchedulesOutlet = Loadable(
  lazy(() => import("../pages/winningSchedules/WinningSchedulesOutlet"))
);
const WinningSchedulesPage = Loadable(
  lazy(() => import("../pages/winningSchedules/WinningSchedulesPage"))
);
const ArchivedWinningSchedulesPage = Loadable(
  lazy(() => import("../pages/winningSchedules/ArchivedWinningSchedulesPage"))
);
const WinningScheduleDetailsPage = Loadable(
  lazy(() => import("../pages/winningSchedules/WinningScheduleDetailsPage"))
);

const widgetsRoute = {
  path: paths.widgets,
  element: <WidgetsOutlet />,
  children: [
    {
      path: `${paths.widget_list}`,
      element: <WidgetListPage />,
    },
    {
      path: paths.create_member_history,
      element: <MemberHistoryPage />,
    },
    {
      path: `${paths.edit_member_history}/:widgetId`,
      element: <MemberHistoryPage />,
    },
    {
      path: paths.create_member_information,
      element: <MemberInformationPage />,
    },
    {
      path: `${paths.edit_member_information}/:widgetId`,
      element: <MemberInformationPage />,
    },
    {
      path: paths.create_list_of_sweepstakes,
      element: <ListOfSweepstakesPage />,
    },
    {
      path: `${paths.edit_list_of_sweepstakes}/:widgetId`,
      element: <ListOfSweepstakesPage />,
    },
    {
      path: paths.create_list_of_campaigns,
      element: <ListOfCampaignsPage />,
    },
    {
      path: `${paths.edit_list_of_campaigns}/:widgetId`,
      element: <ListOfCampaignsPage />,
    },
    {
      path: "",
      element: <Navigate to={`/${paths.widgets}/${paths.widget_list}`} />,
    },
    {
      path: paths.any,
      element: <Navigate to={`/${paths.widgets}/${paths.widget_list}`} />,
    },
  ],
};

const managementRoute = {
  path: paths.system_setting,
  element: <ManagementOutlet />,
  children: [
    {
      index: true,
      element: <SystemSettingPage />,
    },
    {
      path: `${paths.term_and_condition}`,
      element: <TermAndConditionPage />,
    },
    {
      path: `${paths.term_and_condition_view}/:id`,
      element: <TermAndConditionViewPage />,
    },
    {
      path: `${paths.term_and_condition_edit}/:id`,
      element: <TermAndConditionEditPage />,
    },
    {
      path: `${paths.user_login_limits}`,
      element: <UserLimitSettingsPage />,
    },
    {
      path: `${paths.keywords_management}`,
      element: <KeywordManagementPage />,
    },
    {
      path: `${paths.history_logs_view}/:id`,
      element: <HistoryLogsDetailPage />,
    },
    {
      path: paths.history_logs,
      element: <HistoryLogsListPage />,
      children: [
        {
          index: true,
          element: <CampaignTab />,
        },
      ],
    },
    {
      path: "/system-setting/history-logs-content",
      element: <HistoryLogsListPage />,
      children: [
        {
          index: true,
          element: <ContentTab />,
        },
      ],
    },
    {
      path: "/system-setting/history-logs-configuration",
      element: <HistoryLogsListPage />,
      children: [
        {
          index: true,
          element: <ConfigurationTab />,
        },
      ],
    },
    {
      path: `${paths.user_login_limits}/edit`,
      element: <UserLimitSettingsEditPage />,
    },
    {
      path: `${paths.partner_configuration}`,
      element: <PartnerConfigurationViewPage />,
    },
    {
      path: `${paths.partner_configuration}/edit`,
      element: <PartnerConfigurationEditPage />,
    },
    {
      path: `${paths.generic_configuration}`,
      element: <GenericConfigurationViewPage />,
    },
    {
      path: `${paths.generic_configuration}/edit`,
      element: <GenericConfigurationEditPage />,
    },
    {
      path: `${paths.global_outcomes}`,
      element: <OutcomeConfigurationViewPage />,
    },
    {
      path: `${paths.global_outcomes}/edit`,
      element: <OutcomeConfigurationEditPage />,
    },
    {
      path: `${paths.social_media_links}`,
      element: <SocialMediaLinksPage />,
    },
    {
      path: `${paths.social_media_links}/edit`,
      element: <SocialMediaLinksEditPage />,
    },
    {
      path: `${paths.widgets_configuration}`,
      element: <WidgetDetailPage />,
    },
    {
      path: `${paths.widgets_configuration}/edit`,
      element: <div>Widgets Configuration Edit</div>,
    },
    {
      path: `${paths.app_configurations}`,
      element: <AppConfigurationsPage />,
    },
    {
      path: `${paths.app_configurations}/edit`,
      element: <AppConfigurationsEditPage />,
    },
  ],
};

const memberRoute = {
  path: paths.member_list,
  element: <MemberOutlet />,
  children: [
    {
      index: true,
      element: <MemberRelationListPage />,
    },
    {
      path: `${paths.member}/:memberId`,
      element: <MemberRelationDetailPage />,
    },
  ],
};

const campaignsRoute = {
  path: paths.campaigns,
  element: (
    <CampaignSetupProvider>
      <CampaignsPage />
    </CampaignSetupProvider>
  ),
  children: [
    {
      path: paths.campaign_list,
      element: <CampaignListPage />,
    },
    {
      path: `${paths.campaign_summary_view}/:id`,
      element: <CampaignSummaryViewPage />,
    },
    {
      path: paths.campaign_calendar,
      element: <CampaignCalendarPage />,
    },
    {
      path: paths.bonus_list,
      element: <BonusListPage />,
    },
    {
      path: paths.create,

      element: <CampaignSetupOutletPage />,
      children: [
        {
          path: paths.generic_setup,
          element: <GenericSetupPage />,
        },
        {
          path: paths.segment_specific_setup,
          element: <SegmentSetupPage />,
        },
        {
          path: paths.acitivity_level_setup,
          element: <ActivitiesSetupPage />,
        },
        {
          path: paths.acitivity_level_setup_new,
          element: <ActivitiesListOutlet />,
          children: [
            {
              index: true,
              element: <ActivitiesListPage />,
            },
            {
              path: `${paths.acitivity_level_setup_new}/:activityIndex`,
              element: <ActivitySetupPage />,
            },
          ],
        },
        {
          path: paths.campaign_level_setup,
          element: <CampaignLevelSetupPage />,
        },
        {
          path: paths.rules_and_outcomes,
          element: <RulesAndOutcomesPage />,
        },
        {
          path: paths.summary,
          element: <SummaryPage />,
        },
        {
          path: "",
          element: (
            <Navigate
              to={`/${paths.campaigns}/${paths.create}/${paths.generic_setup}`}
            />
          ),
        },
        {
          path: paths.any,
          element: (
            <Navigate
              to={`/${paths.campaigns}/${paths.create}/${paths.generic_setup}`}
            />
          ),
        },
      ],
    },
    {
      path: `${paths.edit}/:campaignId`,

      element: <CampaignSetupOutletPage />,
      children: [
        {
          path: paths.generic_setup,
          element: <GenericSetupPage />,
        },
        {
          path: paths.segment_specific_setup,
          element: <SegmentSetupPage />,
        },
        {
          path: paths.acitivity_level_setup,
          element: <ActivitiesSetupPage />,
        },
        {
          path: paths.acitivity_level_setup_new,
          element: <ActivitiesListOutlet />,
          children: [
            {
              index: true,
              element: <ActivitiesListPage />,
            },
            {
              path: `${paths.acitivity_level_setup_new}/:activityIndex`,
              element: <ActivitySetupPage />,
            },
          ],
        },
        {
          path: paths.campaign_level_setup,
          element: <CampaignLevelSetupPage />,
        },
        {
          path: paths.rules_and_outcomes,
          element: <RulesAndOutcomesPage />,
        },
        {
          path: paths.summary,
          element: <SummaryPage />,
        },
        {
          path: "",
          element: (
            <Navigate
              to={`/${paths.campaigns}/${paths.create}/${paths.generic_setup}`}
            />
          ),
        },
        {
          path: paths.any,
          element: (
            <Navigate
              to={`/${paths.campaigns}/${paths.create}/${paths.generic_setup}`}
            />
          ),
        },
      ],
    },
    {
      path: paths.leaderboards,
      element: <LeaderboardsOutlet />,
      children: [
        {
          index: true,
          element: <LeaderboardsPage />,
        },
        {
          path: `${paths.approve}/:id`,
          element: <LeaderboardsDetailsPage />,
        },
        {
          path: `${paths.archived}`,
          element: <ArchivedLeaderboardsPage />,
        },
      ],
    },
    {
      path: "",
      element: <Navigate to={`/${paths.campaigns}/${paths.campaign_list}`} />,
    },
    {
      path: paths.any,
      element: <Navigate to={`/${paths.campaigns}/${paths.campaign_list}`} />,
    },
  ],
};

const partnersRoute = {
  path: paths.partners,
  element: <CodeGroupsOutlet />,
  children: [
    {
      index: true,
      element: <PartnersPage />,
    },
    // {
    //   path: paths.create,
    //   element: <CreateCodeGroupsPage />,
    // },
  ],
};

const segmentationRoute = {
  path: paths.segmentation,
  element: <SegmentOutlet />,
  children: [
    {
      path: paths.segment_list,
      element: <SegmentListPage />,
      children: [
        {
          index: true,
          element: <SegmentDataTab />,
        },
        {
          path: "list",
          element: <SegmentsListTab />,
        },
        {
          path: paths.platform_segments,
          element: <PlatformSegmentsTab />,
        },
      ],
    },
    {
      path: `${paths.segment_summary_view}/:segmentId`,
      element: <SegmentSummaryViewPage />,
    },
    {
      path: `${paths.segment_list}/${paths.platform_segments}/${paths.edit}/:id`,
      element: <SegmentDetailsPage />,
    },
  ],
};

const rewardsRoute = {
  path: paths.rewards,
  element: <RewardsOutlet />,
  children: [
    {
      index: true,
      element: <RewardsPage />,
    },
    {
      path: paths.rewards_redemption,
      element: (
        <RewardsRedemptionList
          claimStatus={["NOT_APPROVED", "REJECTED", "APPROVED", "FULLY_CLAIMED"]}
        />
      ),
    },
    {
      path: paths.fulfillment_rules,
      element: <FullfillmentRulesPage />,
    },
    {
      path: paths.fulfillment_orders,
      element: <FulfillmentOrdersPage />,
    },
    {
      path: paths.rewards_catalogs,
      element: <RewardsCatalogsListPage />,
    },
    {
      path: `${paths.rewards_catalogs}/${paths.archived}`,
      element: <ArchivedCatalogsPage />,
    },
    {
      path: `${paths.catalog_details}/:id`,
      element: <CatalogDetails />,
    },
    {
      path: `${paths.customized_rewards}/${paths.create}`,
      element: <CreateCustomisedEarnings isEditMode={false} />,
    },
    {
      path: `${paths.customized_rewards}/${paths.edit}/:id`,
      element: <CreateCustomisedEarnings isEditMode={true} />,
    },
    {
      path: paths.customized_rewards,
      element: <CustomizedRewardsListPage rewardType="all" />,
    },
    {
      path: `${paths.customized_reward_details}/:id`,
      element: <CustomizedRewardDetailsPage />,
    },
  ],
};

const codeGroupsRoute = {
  path: paths.code_groups,
  element: <CodeGroupsOutlet />,
  children: [
    {
      index: true,
      element: <CodeGroupsListPage />,
    },
  ],
};
const productsRoute = {
  path: paths.products,
  element: <ProductsPage />,
  children: [
    {
      index: true,
      element: <ProductsPage />,
    },
  ],
};

const winningRewardsScreenRoute = {
  path: paths.winning_rewards_screen,
  element: <WinningRewardsScreen />,
  children: [
    {
      index: true,
      element: <WinningRewardsScreen />,
    },
  ],
};

const contentTypes = ["image", "video", "text"];
const contentTypeRoutes = contentTypes.map((type) => ({
  path: `${paths.create}/${type}`,
  element: <ContentCreationPage contentType={type} />,
}));

const contentRoute = {
  path: paths.content,
  element: <SurveyOutlet />,
  children: [
    {
      path: paths.content_pages,
      element: <ContentOutlet />,
      children: [
        {
          index: true,
          element: <ContentListPage />,
        },
        ...contentTypeRoutes,
        {
          path: `${paths.edit}/:contentId`,
          element: <ContentCreationPage edit={true} />,
        },
        {
          path: `${paths.details}/:contentId`,
          element: <ContentDetailsPage />,
        },
      ],
    },
    {
      path: "content-management/",
      element: <CommonContentPage />,
    },
    {
      path: "content-management/:contentSlug",
      element: <CommonContentPage />,
    },
    {
      path: paths.banners,
      element: (
        <BannerProvider>
          <BannersListPage />
        </BannerProvider>
      ),
    },
    {
      path: paths.survey_list,
      element: (
        <SurveyProvider>
          <SurveyListPage />
        </SurveyProvider>
      ),
    },
    {
      path: paths.create,
      element: <Navigate to={`/${paths.content}/${paths.content_list}`} />,
    },
    {
      path: "",
      element: <Navigate to={`/${paths.content}/${paths.survey_list}`} />,
    },
    {
      path: paths.any,
      element: <Navigate to={`/${paths.content}/${paths.survey_list}`} />,
    },
    {
      path: "",
      element: <Navigate to={`/${paths.content}/${paths.banners}`} />,
    },
    {
      path: paths.any,
      element: <Navigate to={`/${paths.content}/${paths.banners}`} />,
    },
  ],
};

const surveyManagementRoute = {
  path: paths.survey_management,
  element: <SurveyManagementOutlet />,
  children: [
    {
      path: paths.available_surveys,
      element: <SurveyManagementListPage />,
    },
    {
      index: true,
      element: (
        <Navigate to={`/${paths.survey_management}/${paths.available_surveys}`} />
      ),
    },
  ],
};

const sweepstakesRoute = {
  path: paths.sweepstakes,
  element: <SweepstakesOutlet />,
  children: [
    {
      index: true,
      element: <Navigate to={paths.sweepstakes_new} />,
    },
    // {
    //   path: paths.sweepstakes_list,
    //   element: <ActiveSweepstakesPage />,
    // },
    {
      path: paths.entrants_winners,
      element: <SweepstakesEntrantsWinnersPage />,
    },
    {
      path: `${paths.entrants_winners}/${paths.pick_winners}/:sweepstakesId`,
      element: <SweepstakesPickWinnersPage />,
    },
    {
      path: paths.sweepstakes_archiving,
      element: <ArchivesListPage />,
    },
    {
      path: paths.sweepstakes_new,
      element: <SweepstakesDetailsPage />,
    },
    {
      path: `${paths.sweepstakes_new}/${paths.sweepstakes_details}/:sweepstakesId`,
      element: <SweepstakesDrawsAndWinnersPage />,
    },
    {
      path: `${paths.sweepstakes_new}/${paths.sweepstakes_details}/:sweepstakesId/${paths.draw_details}/:drawId`,
      element: <DrawDetailsPage />,
    },
    {
      path: paths.any,
      element: <Navigate to={paths.sweepstakes_new} />,
    },
  ],
};

const callCenterRoute = {
  path: paths.call_center,
  element: <CustomerSupportOutlet />,
  children: [
    {
      index: true,
      element: <Navigate to={paths.member_lookup} />,
    },
    {
      path: paths.member_lookup,
      element: <MemberRelationListPage />,
    },
    {
      path: `${paths.member_lookup}/${paths.member}/:memberId`,
      element: <MemberParticipationDetailsPage />,
    },
    {
      path: `${paths.code_search}`,
      element: <CodeSearchPage />,
    },
  ],
};

const performancesRoute = {
  path: paths.performances_list,
  element: <PerformancesOutlet />,
  children: [
    {
      index: true,
      element: <PerformancesListPage navigationPath={paths.performances_list} />,
    },
    {
      path: `${paths.performance_details}/:performanceID`,
      element: <PerformanceDetailsPage />,
    },
    {
      path: paths.package_scans_performances,
      element: (
        <PerformancesListPage
          engagementType="package-scan"
          navigationPath={paths.package_scans_performances}
        />
      ),
    },
    {
      path: paths.receipt_scan_performances,
      element: <ReceiptScanPerformance />,
    },
    {
      path: paths.code_scan_performances,
      element: (
        <PerformancesListPage
          engagementType="code-scan-generic"
          navigationPath={paths.code_scan_performances}
        />
      ),
    },
    {
      path: paths.code_scan_performances,
      element: (
        <PerformancesListPage
          engagementType="code-scan-generic"
          navigationPath={paths.code_scan_performances}
        />
      ),
    },
    {
      path: paths.qr_scan_performances,
      element: (
        <PerformancesListPage
          engagementType="check-in"
          navigationPath={paths.qr_scan_performances}
        />
      ),
    },
  ],
};

const winningScheduleRoutes = {
  path: paths.winning_schedules,
  element: <WinningSchedulesOutlet />,
  children: [
    {
      path: paths.management,
      element: <WinningSchedulesPage />,
    },
    {
      path: paths.archived,
      element: <ArchivedWinningSchedulesPage />,
    },
    {
      path: `${paths.management}/:id`,
      element: <WinningScheduleDetailsPage />,
    },
    {
      path: "",
      element: <Navigate to={`${paths.winning_schedules}/${paths.management}`} />,
    },
    {
      path: paths.any,
      element: <Navigate to={`${paths.winning_schedules}/${paths.management}`} />,
    },
  ],
};

const tierManagementRoute = {
  path: paths.tier_management,
  element: <TierManagementOutlet />,
  children: [
    {
      index: true,
      element: <TierManagementPage />,
    },
  ],
};

const storeManagementRoute = {
  path: paths.store_management,
  element: <StoreManagementOutlet />,
  children: [
    {
      index: true,
      element: <StoreManagementPage />,
    },
    {
      path: paths.store_create,
      element: <StoreDetailPage />,
    },
    {
      path: `${paths.store_edit}/:id`,
      element: <StoreDetailPage />,
    },
    {
      path: `${paths.store_view}/:id`,
      element: <StoreDetailPage readOnly />,
    },
  ],
};

const productManagementRoute = {
  path: paths.product_management,
  element: <ProductManagementOutlet />,
  children: [
    {
      index: true,
      element: <ProductManagementPage />,
    },
    {
      path: paths.product_create,
      element: <ProductDetailPage />,
    },
    {
      path: `${paths.product_edit}/:id`,
      element: <ProductDetailPage />,
    },
  ],
};

export const config = [
  {
    path: paths.default,
    element: <Layout />,
    children: [
      {
        index: true,
        element: <DashboardPage />,
      },
      {
        ...campaignsRoute,
      },
      {
        ...contentRoute,
      },
      {
        ...surveyManagementRoute,
      },
      {
        ...partnersRoute,
      },
      {
        ...rewardsRoute,
      },
      {
        ...productsRoute,
      },
      {
        ...winningRewardsScreenRoute,
      },
      {
        ...widgetsRoute,
      },
      {
        ...memberRoute,
      },
      {
        ...managementRoute,
      },
      {
        ...sweepstakesRoute,
      },
      {
        ...segmentationRoute,
      },
      {
        ...codeGroupsRoute,
      },
      {
        ...callCenterRoute,
      },
      {
        ...performancesRoute,
      },
      {
        ...winningScheduleRoutes,
      },
      {
        ...storeManagementRoute,
      },
      {
        ...tierManagementRoute,
      },
      {
        ...productManagementRoute,
      },
      { path: paths.any, element: <NotFoundPage /> }, //
    ],
  },
];
